import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useWebSocket, { type Options as UseWebSocketOptions, ReadyState } from 'react-use-websocket';
import { QueryParams } from 'react-use-websocket/dist/lib/types';

import { BACKEND_URL } from '@/constants';
import {
  addActiveStreamAtom,
  removeActiveStreamAtom,
  STREAM_TYPES,
  streamMetadataFamily,
  streamStatusFamily,
  type StreamType,
} from '@/state/stream-status.state';

import { useAgentProxyMode } from './use-agent-proxy-mode';
import { useAuth } from './use-auth';

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_WINDOW_MS = 30000;
const RECONNECT_DELAY_MS = (attemptNumber: number) =>
  Math.min(Math.pow(2, attemptNumber) * 1000, 10000);

const verboseStreamLog = import.meta.env.DEV;

/** Omits pathname in production to limit identifier leakage in client-visible logs. */
const streamLogContext = (streamLabel: string, pathname: string | null | undefined) =>
  verboseStreamLog && pathname ? { stream: streamLabel, pathname } : { stream: streamLabel };

const getReconnectDelayMsParts = (attemptNumber: number, unstableCloseCount: number) => {
  const baseDelayMs = RECONNECT_DELAY_MS(attemptNumber);
  const unstableDelayMs = unstableCloseCount > 0 ? RECONNECT_DELAY_MS(unstableCloseCount) : 0;
  return { baseDelayMs, unstableDelayMs, totalDelayMs: baseDelayMs + unstableDelayMs };
};

export type Result<T, E = unknown> =
  | { ok: true; value: T; error?: never }
  | { ok: false; value?: never; error: E };

/**
 * Options for the useStream hook.
 */
export interface StreamOptions<TRecv, TSend> {
  onReceiveContent: (content: TRecv) => void;
  /// If true, share the same socket for all consumers of a given URL.
  share: boolean;
  /// If provided, use this function to transcode received JSON into a result.
  /// Defaults to JSON.parse. Override to handle Date conversion, etc.
  parse: (json: string) => Result<TRecv>;
  /// If provided, use this function to transcode TSend into a string before transmission.
  /// Defaults to JSON.stringify. Override to handle Date conversion, etc.
  stringify: (content: TSend) => string;
  queryParams?: QueryParams;
  /// Stream type for categorizing this stream (used for stream metadata/diagnostics).
  streamType?: StreamType;
  /// Display name for this stream (used in logging and UI).
  displayName?: string;
}

const FIXED_OPTIONS: UseWebSocketOptions = {
  // Reconnect up to 5 times.
  reconnectAttempts: MAX_RECONNECT_ATTEMPTS,
  retryOnError: true,
};

/**
 * Regular expression to check if the protocol is secure.
 */
const SECURE_PROTOCOL = /https|wss/;

const isKeepaliveEvent = (message: unknown) =>
  typeof message === 'object' &&
  message !== null &&
  'type' in message &&
  (message as { type?: string }).type === 'keepalive';

/**
 * Open a websocket once pathname becomes truthy and keep it open, reconnecting with exponential backoff.
 * This is a wrapper around react-use-websocket that improves typing, handles authorization, and removes choice.
 * It adds content transcoders which can be imported from Speakeasy SDKs to handle things like string-to-Date.
 *
 * Connection status is tracked via Jotai atoms for global observability.
 */
export const useStream = <TReceive, TSend = never>(
  pathname: string | undefined | null,
  options: Partial<StreamOptions<TReceive, TSend>> = {}
) => {
  const { getAccessToken } = useAuth();
  const [token, setToken] = useState<string | null>(null);

  const { isAgentProxyMode } = useAgentProxyMode();
  // Get/set stream status from Jotai atom (single source of truth)
  const statusAtom = useMemo(() => streamStatusFamily(pathname || ''), [pathname]);
  const [streamStatus, setStreamStatus] = useAtom(statusAtom);

  // Get/set stream metadata from Jotai atom
  const metadataAtom = useMemo(() => streamMetadataFamily(pathname || ''), [pathname]);
  const setStreamMetadata = useSetAtom(metadataAtom);

  // Atoms for registering this stream
  const addActiveStream = useSetAtom(addActiveStreamAtom);
  const removeActiveStream = useSetAtom(removeActiveStreamAtom);

  // Reusable token refresh - called on mount and before reconnection attempts
  const refreshToken = useCallback(async () => {
    const token = await getAccessToken();
    setToken(token);
  }, [getAccessToken]);

  // Fetch token on mount
  useEffect(() => {
    refreshToken();
  }, [refreshToken]);

  const { parse, onReceiveContent, share, stringify, queryParams }: StreamOptions<TReceive, TSend> =
    {
      onReceiveContent: () => undefined,
      parse: (json: string) => {
        try {
          return { ok: true, value: JSON.parse(json) };
        } catch (error) {
          return { ok: false, error };
        }
      },
      share: true,
      stringify: JSON.stringify,
      ...options,
    };
  const streamLabel = options.displayName || pathname?.split('/').pop() || pathname || 'unknown';

  // Memoize the WSS-ified URL corresponding to our pathname.
  // If pathname is absent we just have a dummy base URL.
  const url = useMemo(() => {
    const url = new URL(BACKEND_URL.origin);
    url.protocol = SECURE_PROTOCOL.test(url.protocol) ? 'wss:' : 'ws:';
    if (pathname) {
      url.pathname = pathname;
    }
    return url;
  }, [pathname]);

  // Register/unregister as active stream and set metadata
  useEffect(() => {
    if (pathname) {
      addActiveStream(pathname);
      // Set stream metadata (immutable after initialization)
      setStreamMetadata({
        pathname,
        streamType: options.streamType || STREAM_TYPES.OTHER,
        displayName: options.displayName || pathname.split('/').pop() || pathname,
      });
      return () => {
        removeActiveStream(pathname);
      };
    }
  }, [
    pathname,
    addActiveStream,
    removeActiveStream,
    options.streamType,
    options.displayName,
    setStreamMetadata,
  ]);

  const shouldReconnect = useCallback(() => {
    const unstableCount = streamStatus.unstableCloseCount;

    // Check if we've hit the circuit breaker threshold due to unstable connections
    if (unstableCount >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[StreamStatus] Circuit breaker tripped (unstable close threshold reached)', {
        ...streamLogContext(streamLabel, pathname),
        unstableCloseCount: unstableCount,
        threshold: MAX_RECONNECT_ATTEMPTS,
      });
      setStreamStatus((prev) => ({
        ...prev,
        circuitBreakerTripped: true,
        isPendingReconnect: false,
      }));
      return false;
    }

    const now = Date.now();
    const recentHistory = streamStatus.reconnectHistory.filter(
      (timestamp) => now - timestamp < RECONNECT_WINDOW_MS
    );
    const newHistory = [...recentHistory, now];
    const attemptNumber = recentHistory.length; // 0-indexed attempt number
    const { baseDelayMs, unstableDelayMs, totalDelayMs } = getReconnectDelayMsParts(
      attemptNumber,
      streamStatus.unstableCloseCount
    );

    if (import.meta.env.DEV) {
      console.debug('[StreamStatus] Scheduling reconnect attempt', {
        ...streamLogContext(streamLabel, pathname),
        attemptNumber,
        reconnectHistoryCount: newHistory.length,
        unstableCloseCount: unstableCount,
        baseDelayMs,
        unstableDelayMs,
        totalDelayMs,
      });
    }

    setStreamStatus((prev) => ({
      ...prev,
      reconnectHistory: newHistory,
      circuitBreakerTripped: false,
      isPendingReconnect: true,
      reconnectAttemptNumber: attemptNumber,
    }));

    // Refresh token before reconnection attempt
    refreshToken();

    return true;
  }, [
    pathname,
    streamLabel,
    setStreamStatus,
    streamStatus.reconnectHistory,
    streamStatus.unstableCloseCount,
    refreshToken,
  ]);

  // If pathname is not provided, we reject the promise which will cause useWebSocket to retry until the url is valid.
  const getUrl = useCallback(() => {
    if (pathname) {
      return Promise.resolve(url.toString());
    }
    return Promise.reject(new Error('No pathname provided'));
  }, [url, pathname]);

  // Transcode incoming messages and pass them along to the consumer.
  const onMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data) {
        const result = parse(event.data);
        if (result.ok) {
          onReceiveContent(result.value);
          const now = Date.now();
          const isKeepalive = isKeepaliveEvent(result.value);
          setStreamStatus((prev) => {
            const newStreak = isKeepalive ? prev.keepaliveStreak + 1 : 0;
            const nextUnstableCloseCount = newStreak > 2 ? 0 : prev.unstableCloseCount;
            if (prev.unstableCloseCount > 0 && newStreak > 2) {
              console.info('[StreamStatus] Stream stabilized', {
                ...streamLogContext(streamLabel, pathname),
                previousUnstableCloseCount: prev.unstableCloseCount,
                keepaliveStreak: newStreak,
              });
            }
            return {
              ...prev,
              keepaliveStreak: newStreak,
              lastKeepaliveAt: isKeepalive ? now : prev.lastKeepaliveAt,
              unstableCloseCount: nextUnstableCloseCount,
            };
          });
        } else {
          console.error('[StreamStatus] Failed to transcode received message', {
            ...streamLogContext(streamLabel, pathname),
            error: result.error,
          });
        }
      }
    },
    [parse, onReceiveContent, pathname, setStreamStatus, streamLabel]
  );

  // Override reconnectInterval to add extra delay when connection is unstable
  const reconnectInterval = useCallback(
    (attemptNumber: number) =>
      getReconnectDelayMsParts(attemptNumber, streamStatus.unstableCloseCount).totalDelayMs,
    [streamStatus.unstableCloseCount]
  );

  const websocketOptions = {
    ...FIXED_OPTIONS,
    reconnectInterval,
    shouldReconnect,
    filter: (message: MessageEvent) => {
      onMessage(message);
      return false;
    },
    onOpen: () => {
      // readyState is synced separately via useEffect — only reset reconnection bookkeeping here
      setStreamStatus((prev) => ({
        ...prev,
        isPendingReconnect: false,
        circuitBreakerTripped: false,
        reconnectHistory: [],
        lastDisconnectAt: null,
        reconnectAttemptNumber: 0,
        keepaliveStreak: 0,
        lastKeepaliveAt: null,
      }));
    },
    onClose: (event: CloseEvent) => {
      // Use functional updater to avoid stale closure — readyState is synced separately
      const now = Date.now();
      setStreamStatus((prev) => {
        // Skip if we already processed a close (readyState won't be OPEN)
        if (prev.readyState !== ReadyState.OPEN) return prev;
        const nextUnstableCloseCount =
          prev.keepaliveStreak < 2 ? prev.unstableCloseCount + 1 : prev.unstableCloseCount;
        console.warn('[StreamStatus] Stream disconnected', {
          ...streamLogContext(streamLabel, pathname),
          code: event.code,
          wasClean: event.wasClean,
          keepaliveStreak: prev.keepaliveStreak,
          unstableCloseCount: nextUnstableCloseCount,
          ...(verboseStreamLog ? { reason: event.reason || null } : {}),
        });
        return {
          ...prev,
          lastDisconnectAt: now,
          lastCloseEvent: event,
          unstableCloseCount: nextUnstableCloseCount,
        };
      });
    },
    onReconnectStop: (attemptNumber: number) => {
      const now = Date.now();
      console.error('[StreamStatus] Circuit breaker tripped (reconnect attempts exhausted)', {
        ...streamLogContext(streamLabel, pathname),
        attemptNumber,
        maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      });
      setStreamStatus((prev) => {
        const recentHistory = prev.reconnectHistory.filter(
          (timestamp) => now - timestamp < RECONNECT_WINDOW_MS
        );
        return {
          ...prev,
          reconnectHistory: recentHistory,
          circuitBreakerTripped: true,
          isPendingReconnect: false,
          reconnectAttemptNumber: attemptNumber,
        };
      });
    },
    protocols: [
      SECURE_PROTOCOL.test(url.protocol) ? 'wss' : 'ws',
      ...(token ? [`auth.bearer.${token}`] : []),
    ],
    share,
    queryParams,
  };

  // Only connect if pathname is present
  const { sendMessage, readyState } = useWebSocket<TReceive>(
    getUrl,
    websocketOptions,
    !!pathname && (isAgentProxyMode || !!token)
  );

  // Sync readyState from useWebSocket into the Jotai atom — single source of truth
  const prevReadyStateRef = useRef(readyState);
  useEffect(() => {
    if (prevReadyStateRef.current !== readyState) {
      prevReadyStateRef.current = readyState;
      setStreamStatus((prev) => {
        if (prev.readyState === readyState) return prev;
        return { ...prev, readyState };
      });
    }
  }, [readyState, setStreamStatus]);

  // Transcode outgoing messages before sending them on the socket.
  const sendContent = useCallback(
    (content: TSend) => {
      sendMessage(stringify(content));
    },
    [sendMessage, stringify]
  );

  return {
    isConnected: readyState === ReadyState.OPEN,
    circuitBreakerTripped: streamStatus.circuitBreakerTripped,
    sendContent,
  };
};
